import { FirebaseData } from "../General/FirebaseData";
import { CompanyMemberData } from "./CompanyMemberData";
import { LocalData } from "../General/LocalData";
import { CreatedByData } from "../General/CreatedByData";
import { PropertyData } from "../Property/PropertyData";
import { Timestamp } from "firebase/firestore";
import CollectionLoader from "../General/CollectionLoader";
import { LockHolderData } from "./LockHolderData";
import { CompanyModuleData } from "./CompanyModuleData";
import { CompanyCollectionData } from "./Collections/CompanyCollectionData";
import { CompanyIntegrationData } from "./CompanyIntegrationData";
import { CompanyLicenceData } from "./CompanyLicenceData";
import { DataTypeData } from "./DataTypeData";
import { CompanyActionValidatorData } from "./Settings/CompanyActionValidator";
import { CompanyInvoiceReceiverData } from "./CompanyInvoiceReceiverData";
import { CompanyTaskTypeData } from "./CompanyTaskTypeData";
import { EconomicalData } from "./Settings/EconomicalData";

export class CompanyData extends FirebaseData {
  name = "";
  properties: PropertyData[] = [];
  license: "trial" | "customer" | "administrated" | "expired" = "customer";
  administrating: CompanyData[] = [];
  administratedBy: CompanyData[] = [];
  countryIso = "";
  organizationId = "";

  members: CollectionLoader<CompanyMemberData>;
  licences: CollectionLoader<CompanyLicenceData>;
  modules: CollectionLoader<CompanyModuleData>;
  collections: CollectionLoader<CompanyCollectionData>;
  dataTypes: CollectionLoader<DataTypeData>;
  integrations: CollectionLoader<CompanyIntegrationData>;
  actionValidator: CompanyActionValidatorData;
  invoiceReceivers: CollectionLoader<CompanyInvoiceReceiverData>;
  taskTypes: CollectionLoader<CompanyTaskTypeData>;
  economicalData: EconomicalData;
  lockHolders: CollectionLoader<LockHolderData>;

  trialEnd?: Date;

  constructor(id: string, collection: string, parent?: FirebaseData) {
    super(id, collection, parent);
    this.lockHolders = new CollectionLoader("lockHolders", this, LockHolderData);
    this.members = new CollectionLoader("members", this, CompanyMemberData);
    this.licences = new CollectionLoader("licences", this, CompanyLicenceData);
    this.modules = new CollectionLoader("modules", this, CompanyModuleData);
    this.integrations = new CollectionLoader("integrations", this, CompanyIntegrationData);
    this.collections = new CollectionLoader("collections", this, CompanyCollectionData);
    this.dataTypes = new CollectionLoader("dataTypes", this, DataTypeData);
    this.actionValidator = new CompanyActionValidatorData("actionValidator", "settings", this);
    this.invoiceReceivers = new CollectionLoader("invoiceReceivers", this, CompanyInvoiceReceiverData);
    this.taskTypes = new CollectionLoader("taskTypes", this, CompanyTaskTypeData);
    this.economicalData = new EconomicalData("economical", "settings", this);
  }

  setup(data: any) {
    if (super.isValidString(data.name)) {
      this.name = data.name;
    }
    if (super.isValidString(data.countryIso)) {
      this.countryIso = data.countryIso;
    }
    this.administrating = Array.isArray(data.administrating) ? data.administrating.map((id: string) => LocalData.companies.addData(id)) : [];
    this.administratedBy = Array.isArray(data.administratedBy) ? data.administratedBy.map((id: string) => LocalData.companies.addData(id)) : [];

    if (super.isValidString(data.license)) {
      this.license = data.license;
    }
    if (super.isValidString(data.organizationId)) {
      this.organizationId = data.organizationId;
    }
    if (super.isValidObject(data.createdBy)) {
      this.createdBy = new CreatedByData(data.createdBy);
    }
    if (Array.isArray(data.properties)) {
      this.properties = [];
      (data.properties as any[]).forEach((id) => {
        if (super.isValidString(id)) {
          this.properties.push(LocalData.properties.addData(id));
        }
      });
    }

    if (data.trialEnd as Timestamp) {
      try {
        this.trialEnd = (data.trialEnd as Timestamp).toDate();
      } catch (error) {
        this.trialEnd = undefined;
      }
    }
    if (this.isValidDate(data.trialEnd)) {
      this.trialEnd = data.trialEnd;
    }

    super.setup(data);
  }

  trialEnded(): boolean {
    if (this.trialEnd) {
      const trialEnd = new Date(this.trialEnd.getTime());
      trialEnd.setMilliseconds(0);
      trialEnd.setSeconds(0);
      trialEnd.setMinutes(0);
      trialEnd.setHours(0);

      const today = new Date();
      today.setMilliseconds(0);
      today.setSeconds(0);
      today.setMinutes(0);
      today.setHours(0);

      if (trialEnd > today) {
        return true;
      }
    }
    return false;
  }

  deleteCompany(completion: (error?: Error) => void) {
    this.deleteDocumentWithCompletionHandler((error) => {
      if (error) {
        completion(error);
      } else {
        LocalData.currentUser.companies = LocalData.currentUser.companies.filter((company) => company !== this);
        this.deleted = true;
        completion();
      }
    });
  }
}
